import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { callAuthedSupabaseFunction } from '../../lib/api'
import { supabase } from '../../lib/supabase'

interface MetaGalleryStatus {
  ok?: boolean
  status?: 'setup_required' | 'not_connected' | 'pending_page' | 'connected'
  facebookPageName?: string
  instagramUsername?: string
  lastSyncedAt?: string | null
  lastError?: string | null
  instructions?: string
  pages?: Array<{ id: string; name: string; instagramUsername?: string | null }>
  media?: Array<{
    media_id: string
    caption: string
    permalink: string | null
    cached_url: string | null
    source_thumbnail_url: string | null
    visibility: string
    hashtag_matched: boolean
    source_status: string
  }>
  error?: string
  authorizeUrl?: string
}

export function MetaGalleryPanel() {
  const [status, setStatus] = useState<MetaGalleryStatus | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) return
    const response = await callAuthedSupabaseFunction('meta-gallery', accessToken)
    const payload = await response.json() as MetaGalleryStatus
    setStatus(payload)
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('meta') === 'error') setError(params.get('reason') || 'Meta authorisation failed.')
    if (params.get('meta') === 'connected') setMessage('Instagram Professional account connected.')
    void load()
  }, [load])

  async function post(body: Record<string, unknown>) {
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) throw new Error('Sign in again.')
    const response = await callAuthedSupabaseFunction('meta-gallery', accessToken, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return await response.json() as MetaGalleryStatus
  }

  async function connect() {
    setBusy(true)
    try {
      const { data } = await supabase.auth.getSession()
      const accessToken = data.session?.access_token
      if (!accessToken) throw new Error('Sign in again.')
      const response = await callAuthedSupabaseFunction('meta-oauth-start', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })
      const payload = await response.json() as MetaGalleryStatus
      if (!payload.authorizeUrl) throw new Error(payload.error || 'Meta did not return an authorisation URL.')
      window.location.assign(payload.authorizeUrl)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Meta connect failed.')
      setBusy(false)
    }
  }

  async function selectPage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const pageId = new FormData(event.currentTarget).get('pageId')
    if (typeof pageId !== 'string') return
    setBusy(true)
    try {
      const payload = await post({ action: 'select_page', pageId })
      if (!payload.ok) throw new Error(payload.error || 'Page was not selected.')
      setStatus(payload)
      setMessage('Instagram account selected.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Page was not selected.')
    } finally {
      setBusy(false)
    }
  }

  async function syncNow() {
    setBusy(true)
    setError(null)
    try {
      const payload = await post({ action: 'sync' })
      if (!payload.ok) throw new Error(payload.error || 'Sync failed.')
      setMessage('Gallery synced from Instagram.')
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sync failed.')
    } finally {
      setBusy(false)
    }
  }

  async function setVisibility(mediaId: string, visibility: string) {
    await post({ action: 'set_visibility', mediaId, visibility })
    await load()
  }

  const connectionStatus = status?.status ?? 'not_connected'
  const statusLabel = connectionStatus === 'connected'
    ? 'Connected'
    : connectionStatus === 'pending_page'
      ? 'Connected — choose Instagram account'
      : connectionStatus === 'setup_required'
        ? 'Not connected / not configured'
        : 'Instagram not connected'
  const media = status?.media ?? []
  const lastSync = status?.lastSyncedAt
    ? `Last sync ${status.lastSyncedAt}`
    : 'Last sync: never'

  return (
    <article className="admin-card">
      <div className="admin-card-heading">
        <div>
          <h2>Gallery / Instagram</h2>
          <p>{status?.instagramUsername || status?.facebookPageName || 'Instagram not connected'}</p>
        </div>
        <span className="status-pill">{statusLabel}</span>
      </div>
      {message ? <p className="admin-alert">{message}</p> : null}
      {error || status?.lastError ? <p className="admin-alert error">{error || status?.lastError}</p> : null}
      <p className="admin-empty-state">{lastSync}</p>
      {connectionStatus === 'setup_required' ? <p className="admin-empty-state">{status?.instructions}</p> : null}
      {connectionStatus === 'pending_page' && (status?.pages?.length ?? 0) > 0 ? (
        <form className="admin-edit-grid" onSubmit={(event) => void selectPage(event)}>
          <label>
            Facebook Page with linked Instagram
            <select name="pageId" required defaultValue="">
              <option value="" disabled>Select page</option>
              {status?.pages?.map((page) => (
                <option key={page.id} value={page.id}>
                  {page.name}{page.instagramUsername ? ` · @${page.instagramUsername}` : ''}
                </option>
              ))}
            </select>
          </label>
          <button disabled={busy} type="submit">Use this account</button>
        </form>
      ) : null}
      <div className="booking-contact-actions">
        <button disabled={busy || connectionStatus === 'setup_required'} onClick={() => void connect()} type="button">
          Connect Instagram
        </button>
        <button disabled={busy || connectionStatus !== 'connected'} onClick={() => void syncNow()} type="button">
          Sync now
        </button>
      </div>
      {media.map((item) => (
        <div className="review-list" key={item.media_id}>
          <div>
            <dt>{item.caption || item.media_id}</dt>
            <dd>
              {item.permalink ? <a href={item.permalink} rel="noreferrer" target="_blank">Open source</a> : 'No permalink'}
              {' · '}
              <select
                onChange={(event) => void setVisibility(item.media_id, event.target.value)}
                value={item.visibility}
              >
                <option value="auto">Auto (#autoglowgallery)</option>
                <option value="published">Show</option>
                <option value="hidden">Hide</option>
              </select>
            </dd>
          </div>
        </div>
      ))}
    </article>
  )
}
